/* eslint-disable max-lines */
import { MonitoringDisarmInfo } from '@hakimo-ui/hakimo/feature-shared';
import { CameraDetail } from '@hakimo-ui/hakimo/types';
import {
  convertToMinutes,
  useCanUpdateArmedStatus,
  useCanUpdateCamera,
} from '@hakimo-ui/hakimo/util';
import { DetailField, DetailSection } from '@hakimo-ui/shared/ui-base';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useOverviewModals } from './useOverviewModals';

dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

const AUTOMATED_TALKDOWN_TIMEZONE = 'UTC';

interface Props {
  camera: CameraDetail;
}

export function OverviewDetails(props: Props) {
  const { camera } = props;
  const canUpdateCamera = useCanUpdateCamera();
  const canUpdateArmedStatus = useCanUpdateArmedStatus();
  const modals = useOverviewModals(camera);
  const disarmWindow = camera.armStatus.cameraDisarmWindow
    ? {
        startTimestamp: camera.armStatus.cameraDisarmWindow.startTime,
        endTimestamp: camera.armStatus.cameraDisarmWindow.endTime,
        timezone: camera.armStatus.cameraDisarmWindow.timezone,
      }
    : null;

  const getCameraLocation = () => {
    if (!camera.location) {
      return 'Not specified';
    }
    const { name, city, state, country } = camera.location;
    const locationValues = [name, city, state, country].filter(Boolean);
    return locationValues.join(', ');
  };

  const getStatusValue = () => {
    const statusValue = camera.armStatus.endTimeText ? (
      <span>{camera.armStatus.endTimeText}</span>
    ) : camera.armStatus.isArmed ? (
      <span className="text-status-orange">Armed</span>
    ) : (
      <span className="text-status-green">Disarmed</span>
    );

    if (!disarmWindow) {
      return statusValue;
    }

    return (
      <div className="flex flex-col gap-1">
        {statusValue}
        <MonitoringDisarmInfo
          disarmWindow={disarmWindow}
          title="Temporary Monitoring Disarmed"
        />
      </div>
    );
  };

  const getAutomatedTalkdownValue = () => {
    const { automatedTalkdownStartTimeUTC, automatedTalkdownEndTimeUTC } =
      camera;

    if (!automatedTalkdownEndTimeUTC && !automatedTalkdownStartTimeUTC) {
      return "As per location's monitoring window";
    }

    const startTime = automatedTalkdownStartTimeUTC || '';
    const endTime = automatedTalkdownEndTimeUTC || '';
    const showPlusOneDay =
      startTime &&
      endTime &&
      convertToMinutes(startTime) > convertToMinutes(endTime);

    return (
      <div className="flex flex-col">
        <span>
          Start Time: {startTime || 'NOT SET'}{' '}
          {automatedTalkdownStartTimeUTC && AUTOMATED_TALKDOWN_TIMEZONE}
        </span>
        <span>
          End Time: {endTime || 'NOT SET'}{' '}
          {automatedTalkdownEndTimeUTC && AUTOMATED_TALKDOWN_TIMEZONE}{' '}
          {showPlusOneDay && '(Next Day)'}
        </span>
      </div>
    );
  };

  const getFormattedLastUpdated = () => {
    if (!camera.updated_at_utc) {
      return '-';
    }
    const timezone = camera.location?.timezone || 'UTC';
    return dayjs(camera.updated_at_utc)
      .tz(timezone)
      .format('DD/MM/YYYY, HH:mm:ss');
  };

  return (
    <>
      <DetailSection title="Overview">
        <DetailField
          label="Name"
          value={camera.name}
          onEdit={canUpdateCamera ? modals.openUpdateName : undefined}
          readOnly={!canUpdateCamera}
        />
        <DetailField
          label="Status"
          value={getStatusValue()}
          onEdit={
            canUpdateArmedStatus ? modals.openUpdateArmedStatus : undefined
          }
          readOnly={!canUpdateArmedStatus}
        />
        <DetailField
          label="Automated Talkdown"
          value={getAutomatedTalkdownValue()}
          onEdit={
            canUpdateCamera ? modals.openUpdateAutomatedTalkdown : undefined
          }
          readOnly={!canUpdateCamera}
        />
        <DetailField
          label="Livestream URL"
          value={camera.livestreamUrl}
          onEdit={canUpdateCamera ? modals.openUpdateLivestreamUrl : undefined}
          readOnly={!canUpdateCamera}
        />
        <DetailField
          label="RTSP URL"
          value={camera.rtspUrl}
          onEdit={canUpdateCamera ? modals.openUpdateRtspUrl : undefined}
          readOnly={!canUpdateCamera}
        />
        <DetailField
          label="Location"
          value={getCameraLocation()}
          onEdit={canUpdateCamera ? modals.openUpdateLocation : undefined}
          readOnly={!canUpdateCamera}
        />
        <DetailField
          label="Last Updated"
          value={getFormattedLastUpdated()}
          readOnly={true}
        />
      </DetailSection>
      {modals.renderModals()}
    </>
  );
}

export default OverviewDetails;
